import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { LockOutlined, MailOutlined } from '@mui/icons-material';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Google as GoogleIcon,
} from '@mui/icons-material';
import { authService } from '../../services/authService';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [socialLoginMessage, setSocialLoginMessage] = useState('');
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [googleReady, setGoogleReady] = useState(false);

  useEffect(() => {
    document.title = 'CryptoNovaX - ' + t('auth.login.title');
  }, [t]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const scriptId = 'google-identity-services';
    if (document.getElementById(scriptId)) {
      setGoogleReady(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.id = scriptId;
    script.onload = () => setGoogleReady(true);
    document.body.appendChild(script);
  }, []);

  const handleGoogleLogin = async () => {
    try {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID as string | undefined;
      if (!clientId) {
        setSocialLoginMessage(t('auth.login.socialNotAvailable'));
        return;
      }

      const google = (window as any).google;
      if (!google || !google.accounts || !google.accounts.id) {
        const code = await openGooglePopupForCode(clientId);
        if (code) {
          const loginRes = await authService.googleLoginWithCode(code);
          if (loginRes.success) {
            if (loginRes.needsOnboarding) {
              switch (loginRes.onboardingStep) {
                case 'language':
                  navigate('/onboarding/language');
                  break;
                case 'capital':
                  navigate('/onboarding/capital');
                  break;
                case 'complete':
                  navigate('/onboarding/complete');
                  break;
                default:
                  navigate('/onboarding/language');
              }
            } else {
              navigate('/dashboard');
            }
          } else {
            setError(loginRes.message || t('auth.login.error'));
          }
        } else {
          setSocialLoginMessage(t('auth.login.socialNotAvailable'));
        }
        return;
      }

      await new Promise<void>((resolve, reject) => {
        try {
          google.accounts.id.initialize({
            client_id: clientId,
            callback: async (response: any) => {
              try {
                const idToken = response.credential;
                if (!idToken) {
                  reject(new Error('Identifiant Google manquant'));
                  return;
                }
                const loginRes = await authService.googleLogin(idToken);
                if (loginRes.success) {
                  if (loginRes.needsOnboarding) {
                    switch (loginRes.onboardingStep) {
                      case 'language':
                        navigate('/onboarding/language');
                        break;
                      case 'capital':
                        navigate('/onboarding/capital');
                        break;
                      case 'complete':
                        navigate('/onboarding/complete');
                        break;
                      default:
                        navigate('/onboarding/language');
                    }
                  } else {
                    navigate('/dashboard');
                  }
                  resolve();
                } else {
                  setError(loginRes.message || t('auth.login.error'));
                  reject(new Error(loginRes.message || 'Google login failed'));
                }
              } catch (err: any) {
                setError(err.message || t('auth.login.error'));
                reject(err);
              }
            },
            auto_select: false,
            cancel_on_tap_outside: true,
          });

          google.accounts.id.prompt(async (notification: any) => {
            if (notification && notification.isNotDisplayed) {
              try {
                const code = await openGooglePopupForCode(clientId);
                if (code) {
                  const loginRes = await authService.googleLoginWithCode(code);
                  if (loginRes.success) {
                    if (loginRes.needsOnboarding) {
                      switch (loginRes.onboardingStep) {
                        case 'language':
                          navigate('/onboarding/language');
                          break;
                        case 'capital':
                          navigate('/onboarding/capital');
                          break;
                        case 'complete':
                          navigate('/onboarding/complete');
                          break;
                        default:
                          navigate('/onboarding/language');
                      }
                    } else {
                      navigate('/dashboard');
                    }
                    resolve();
                  } else {
                    setError(loginRes.message || t('auth.login.error'));
                    reject(new Error(loginRes.message || 'Google login failed'));
                  }
                } else {
                  setSocialLoginMessage(t('auth.login.socialNotAvailable'));
                  reject(new Error('Google prompt not displayed'));
                }
              } catch (err) {
                setSocialLoginMessage(t('auth.login.socialNotAvailable'));
                reject(err);
              }
            }
          });
        } catch (e) {
          reject(e);
        }
      });
    } catch (e) {
      // noop: erreurs déjà gérées
    }
  };

  const openGooglePopupForCode = (clientId: string): Promise<string | null> => {
    return new Promise((resolve) => {
      const redirectUri = window.location.origin + '/auth/google/callback';
      const scope = 'openid email profile';
      const state = Math.random().toString(36).slice(2);
      const codeChallengeMethod = 'plain';
      const codeVerifier = Math.random().toString(36).slice(2);
      const codeChallenge = codeVerifier;

      const oauthUrl = `https://accounts.google.com/o/oauth2/v2/auth?response_type=code&client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(scope)}&state=${encodeURIComponent(state)}&code_challenge_method=${encodeURIComponent(codeChallengeMethod)}&code_challenge=${encodeURIComponent(codeChallenge)}`;

      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        oauthUrl,
        'google_oauth_popup',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        resolve(null);
        return;
      }

      const timer = setInterval(() => {
        try {
          if (popup.closed) {
            clearInterval(timer);
            resolve(null);
            return;
          }
          const url = popup.location.href;
          if (url && url.startsWith(redirectUri)) {
            const params = new URL(url).searchParams;
            const code = params.get('code');
            const returnedState = params.get('state');
            if (code && returnedState === state) {
              clearInterval(timer);
              popup.close();
              resolve(code);
              return;
            }
          }
        } catch {
          // cross-origin until redirected back
        }
      }, 300);
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) setError('');
  };

  const handleCloseSnackbar = () => {
    setSocialLoginMessage('');
    setForgotPasswordMessage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authService.login(formData.email, formData.password);
      if (response.success) {
        if (response.needsOnboarding) {
          switch (response.onboardingStep) {
            case 'language':
              navigate('/onboarding/language');
              break;
            case 'capital':
              navigate('/onboarding/capital');
              break;
            case 'complete':
              navigate('/onboarding/complete');
              break;
            default:
              navigate('/onboarding/language');
          }
        } else {
          navigate('/dashboard');
        }
      } else {
        setError(response.message || t('auth.login.error'));
      }
    } catch (error: any) {
      console.error('Erreur de connexion:', error);
      setError(error.message || t('auth.login.serverError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'stretch',
        background: '#ffffff',
        p: 0,
        m: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: '100vh',
          backgroundColor: '#ffffff',
          overflow: 'hidden',
        }}
      >
        {/* Left Column - Form */}
        <Box
          sx={{
            width: { xs: '100%', md: '50%' },
            p: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#ffffff',
          }}
        >
          <Box sx={{ width: '100%', maxWidth: '340px' }}>
            <Box sx={{ mb: 5, textAlign: 'center' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666', 
                  mb: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: 400,
                  letterSpacing: '0.02em',
                }}
              >
                {t('auth.login.heroTitle')}
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ 
                  mb: 0, 
                  color: '#000',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  letterSpacing: '-0.02em',
                }}
              >
                CryptoNovaX
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<GoogleIcon sx={{ fontSize: '16px' }} />}
                onClick={handleGoogleLogin}
                sx={{
                  backgroundColor: '#1a1a1a',
                  color: '#ffffff',
                  '&:hover': { 
                    backgroundColor: '#2a2a2a',
                  },
                  borderRadius: '8px',
                  height: '48px',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  boxShadow: 'none',
                  '&.Mui-disabled': {
                    backgroundColor: '#1a1a1a',
                    color: '#ffffff',
                    opacity: 0.7,
                  },
                }}
                disabled={!googleReady}
              >
                {t('auth.login.continueGoogle')}
              </Button>
            </Box>

            <Box sx={{ position: 'relative', my: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: 0,
                  right: 0,
                  height: '1px',
                  backgroundColor: '#e0e0e0',
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  position: 'relative',
                  backgroundColor: '#ffffff',
                  px: 2,
                  color: '#999',
                  fontSize: '0.75rem',
                }}
              >
                {t('auth.login.orEmail')}
              </Typography>
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 2.5,
                  backgroundColor: 'rgba(244, 67, 54, 0.08)',
                  border: '1px solid rgba(244, 67, 54, 0.2)',
                  color: '#d32f2f',
                  borderRadius: '8px',
                }}
              >
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
                placeholder={t('auth.login.emailLabel')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutlined sx={{ color: '#999', fontSize: '20px' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f8f8f8',
                    borderRadius: '8px',
                    height: '48px',
                    border: '1px solid #e0e0e0',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#d0d0d0',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                      borderColor: '#1a1a1a',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#000',
                    fontSize: '0.875rem',
                    '&::placeholder': {
                      color: '#999',
                      opacity: 1,
                    },
                  },
                }}
              />

              <TextField
                fullWidth
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                required
                disabled={loading}
                placeholder={t('auth.login.passwordLabel')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: '#999', fontSize: '20px' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: '#999' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f8f8f8',
                    borderRadius: '8px',
                    height: '48px',
                    border: '1px solid #e0e0e0',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#d0d0d0',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                      borderColor: '#1a1a1a',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#000',
                    fontSize: '0.875rem',
                    '&::placeholder': {
                      color: '#999',
                      opacity: 1,
                    },
                  },
                }}
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  backgroundColor: '#f8f8f8',
                  color: '#000',
                  height: '48px',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  mb: 3,
                  boxShadow: 'none',
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    backgroundColor: '#ebebeb',
                    boxShadow: 'none',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#f8f8f8',
                    color: '#999',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : t('auth.login.title')}
              </Button>
            </Box>

            <Box sx={{ textAlign: 'center', mb: 2.5 }}>
              <Link
                to="/forgot-password"
                style={{
                  color: '#000',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  fontWeight: 400,
                }}
              >
                {t('auth.login.forgotPassword')}
              </Link>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#666', fontSize: '0.75rem' }}>
                {t('auth.login.noAccount')}{' '}
                <Link
                  to="/register"
                  style={{
                    color: '#000',
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                >
                  {t('auth.login.registerCta')}
                </Link>
              </Typography>
            </Box>

            <Snackbar open={!!socialLoginMessage} autoHideDuration={6000} onClose={handleCloseSnackbar}>
              <Alert onClose={handleCloseSnackbar} severity="info" sx={{ width: '100%' }}>
                {socialLoginMessage}
              </Alert>
            </Snackbar>

            <Snackbar open={!!forgotPasswordMessage} autoHideDuration={6000} onClose={handleCloseSnackbar}>
              <Alert onClose={handleCloseSnackbar} severity="info" sx={{ width: '100%' }}>
                {forgotPasswordMessage}
              </Alert>
            </Snackbar>
          </Box>
        </Box>

        {/* Right Column - Image */}
        <Box
          sx={{
            width: { xs: '0%', md: '50%' },
            display: { xs: 'none', md: 'block' },
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(180deg, #4a6fa5 0%, #5b7db8 15%, #d97c91 45%, #e85d72 65%, #1a1a1a 100%)',
          }}
        >
          {/* Overlay shapes */}
          <Box
            sx={{
              position: 'absolute',
              top: '15%',
              right: '10%',
              width: '300px',
              height: '400px',
              background: 'linear-gradient(135deg, rgba(255, 100, 120, 0.6) 0%, rgba(255, 100, 120, 0.3) 100%)',
              filter: 'blur(80px)',
              transform: 'rotate(-15deg)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '25%',
              left: '15%',
              width: '250px',
              height: '350px',
              background: 'linear-gradient(135deg, rgba(255, 100, 120, 0.5) 0%, rgba(255, 100, 120, 0.2) 100%)',
              filter: 'blur(70px)',
              transform: 'rotate(25deg)',
            }}
          />

          {/* Trust badges - Center */}
          <Box sx={{ 
            position: 'absolute',
            top: '50%',
            left: '55%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 3.5,
            width: '85%',
            maxWidth: '420px',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box sx={{ 
                fontSize: '2rem', 
                lineHeight: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                width: '56px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}>
                🔒
              </Box>
              <Box>
                <Typography sx={{ 
                  fontSize: '1.125rem', 
                  fontWeight: 600, 
                  color: '#ffffff',
                  mb: 0.5,
                  lineHeight: 1.3,
                }}>
                  {t('auth.login.uspEncrypted')}
                </Typography>
                <Typography sx={{ 
                  fontSize: '0.875rem', 
                  color: 'rgba(255, 255, 255, 0.75)',
                  lineHeight: 1.5,
                }}>
                  {t('auth.login.uspEncryptedSubtitle')}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box sx={{ 
                fontSize: '2rem', 
                lineHeight: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                width: '56px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}>
                ⭐
              </Box>
              <Box>
                <Typography sx={{ 
                  fontSize: '1.125rem', 
                  fontWeight: 600, 
                  color: '#ffffff',
                  mb: 0.5,
                  lineHeight: 1.3,
                }}>
                  {t('auth.login.uspRated')}
                </Typography>
                <Typography sx={{ 
                  fontSize: '0.875rem', 
                  color: 'rgba(255, 255, 255, 0.75)',
                  lineHeight: 1.5,
                }}>
                  {t('auth.login.uspRatedSubtitle')}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box sx={{ 
                fontSize: '2rem', 
                lineHeight: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                width: '56px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}>
                🏦
              </Box>
              <Box>
                <Typography sx={{ 
                  fontSize: '1.125rem', 
                  fontWeight: 600, 
                  color: '#ffffff',
                  mb: 0.5,
                  lineHeight: 1.3,
                }}>
                  {t('auth.login.uspBanks')}
                </Typography>
                <Typography sx={{ 
                  fontSize: '0.875rem', 
                  color: 'rgba(255, 255, 255, 0.75)',
                  lineHeight: 1.5,
                }}>
                  {t('auth.login.uspBanksSubtitle')}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box sx={{ 
                fontSize: '2rem', 
                lineHeight: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                width: '56px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}>
                📱
              </Box>
              <Box>
                <Typography sx={{ 
                  fontSize: '1.125rem', 
                  fontWeight: 600, 
                  color: '#ffffff',
                  mb: 0.5,
                  lineHeight: 1.3,
                }}>
                  {t('auth.login.uspAvailable')}
                </Typography>
                <Typography sx={{ 
                  fontSize: '0.875rem', 
                  color: 'rgba(255, 255, 255, 0.75)',
                  lineHeight: 1.5,
                }}>
                  {t('auth.login.uspAvailableSubtitle')}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {/* Bottom attribution */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 32,
              left: 32,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1.5px solid rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: '#ffffff',
                fontSize: '0.6875rem',
                fontWeight: 400,
                letterSpacing: '0.01em',
                opacity: 0.9,
              }}
            >
              {t('auth.login.heroSubtitle')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;